import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { TenantLayout, useTenant } from "@/components/tenant/tenant-layout";
import { downloadCsv, listAuditEvents } from "@/lib/api/audit";
import { listFunds } from "@/lib/api/funds";

const ENTITY_FILTERS = [
  "",
  "documents",
  "provisions",
  "mfn_clauses",
  "mfn_triggers",
  "obligations",
  "obligation_completions",
  "funds",
  "lps",
] as const;

function AuditInner() {
  const tenant = useTenant();
  const [entityType, setEntityType] = useState("");

  const { data: funds } = useQuery({
    queryKey: ["funds", tenant.id],
    queryFn: () => listFunds(tenant.id),
  });

  const {
    data: events,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["audit", tenant.id, entityType],
    queryFn: () =>
      listAuditEvents(tenant.id, entityType ? { limit: 500, entityType } : { limit: 500 }),
  });

  return (
    <>
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Audit trail</h1>
          <p className="text-sm text-neutral-400">
            Append-only at the database level — every state change, who did it, and when. This is
            your audit evidence.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {funds?.map((f) => (
            <Link
              key={f.id}
              to="/t/$slug/audit-pack/$fundId"
              params={{ slug: tenant.slug, fundId: f.id }}
              className="rounded border border-neutral-700 px-3 py-1.5 text-xs text-neutral-300 hover:bg-neutral-800"
            >
              Audit pack: {f.short_name ?? f.name}
            </Link>
          ))}
          <button
            type="button"
            disabled={!events || events.length === 0}
            onClick={() =>
              downloadCsv(
                `argus-audit-${tenant.slug}-${new Date().toISOString().slice(0, 10)}.csv`,
                (events ?? []).map((e) => ({
                  id: e.id,
                  timestamp: e.created_at,
                  actor_type: e.actor_type,
                  actor_id: e.actor_id,
                  action: e.action,
                  entity_type: e.entity_type,
                  entity_id: e.entity_id,
                })),
              )
            }
            className="rounded bg-neutral-100 px-3 py-1.5 text-xs font-medium text-neutral-950 hover:bg-white disabled:opacity-50"
          >
            Export CSV
          </button>
        </div>
      </header>

      <div className="flex items-center gap-2 text-xs">
        <label htmlFor="entity-filter" className="text-neutral-500">
          Entity
        </label>
        <select
          id="entity-filter"
          value={entityType}
          onChange={(e) => setEntityType(e.target.value)}
          className="rounded border border-neutral-800 bg-neutral-900 px-2 py-1 text-neutral-200"
        >
          {ENTITY_FILTERS.map((f) => (
            <option key={f} value={f}>
              {f === "" ? "All entities" : f}
            </option>
          ))}
        </select>
        <span className="text-neutral-600">Showing the latest 500 events.</span>
      </div>

      {isLoading ? (
        <p className="text-sm text-neutral-500">Loading audit events…</p>
      ) : error ? (
        <div className="rounded border border-red-900/60 bg-red-950/30 px-3 py-2 text-xs text-red-300">
          {error.message}
        </div>
      ) : !events || events.length === 0 ? (
        <div className="rounded border border-neutral-800 bg-neutral-900 p-6 text-center text-sm text-neutral-400">
          No audit events match this filter.
        </div>
      ) : (
        <div className="overflow-hidden rounded border border-neutral-800">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-neutral-800 bg-neutral-900/50 text-[11px] uppercase tracking-wide text-neutral-500">
              <tr>
                <th className="px-3 py-2">When</th>
                <th className="px-3 py-2">Actor</th>
                <th className="px-3 py-2">Action</th>
                <th className="px-3 py-2">Entity</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-900">
              {events.map((e) => (
                <tr key={e.id} className="hover:bg-neutral-900/50">
                  <td className="whitespace-nowrap px-3 py-2 text-neutral-400">
                    {new Date(e.created_at).toLocaleString()}
                  </td>
                  <td className="px-3 py-2 text-neutral-400">{e.actor_type}</td>
                  <td className="px-3 py-2 font-mono text-[12px] text-neutral-300">{e.action}</td>
                  <td className="px-3 py-2 text-neutral-500">
                    {e.entity_type}
                    {e.entity_id ? (
                      <span className="text-neutral-700"> · {e.entity_id.slice(0, 8)}…</span>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

export function AuditPage() {
  return (
    <TenantLayout>
      <AuditInner />
    </TenantLayout>
  );
}
